import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileImage, FileText, Lock, Paperclip, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { EmptyState } from "@/components/common/EmptyState";
import { LoadingList } from "@/components/common/LoadingList";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage, openFile } from "@/lib/api";
import { fmtBytes, fmtDateTime } from "@/lib/format";

const ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf";

export const VedleggPanel = ({ order, canEdit }) => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const fileRef = useRef(null);
  const [note, setNote] = useState("");
  const [adminOnly, setAdminOnly] = useState(false);

  const query = useQuery({ queryKey: ["attachments", order.id], queryFn: async () => (await api.get(`/orders/${order.id}/attachments`)).data });

  const upload = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      if (note) fd.append("note", note);
      if (isAdmin && adminOnly) fd.append("admin_only", "true");
      return (await api.post(`/orders/${order.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attachments", order.id] });
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      queryClient.invalidateQueries({ queryKey: ["history", order.id] });
      toast.success("Vedlegg lasta opp", { description: data.filename });
      setNote("");
      setAdminOnly(false);
      if (fileRef.current) fileRef.current.value = "";
    },
    onError: (err) => toast.error("Opplasting feila", { description: errorMessage(err) }),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/attachments/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attachments", order.id] });
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      toast.success("Vedlegget er sletta");
    },
    onError: (err) => toast.error("Kunne ikkje slette", { description: errorMessage(err) }),
  });

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fila er for stor", { description: "Maks 10 MB per fil." });
      e.target.value = "";
      return;
    }
    upload.mutate(file);
  };

  const view = async (att) => {
    try {
      await openFile(`/attachments/${att.id}/download`);
    } catch (err) {
      toast.error("Kunne ikkje opne vedlegget", { description: errorMessage(err) });
    }
  };

  return (
    <div className="space-y-4" data-testid="vedlegg-panel">
      {canEdit ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <div className="space-y-2">
              <Label htmlFor="vedlegg-note">Merknad til vedlegget (valfritt)</Label>
              <Input id="vedlegg-note" value={note} onChange={(e) => setNote(e.target.value)} className="h-11 bg-card" placeholder="t.d. Bilete av ferdig grøft" data-testid="vedlegg-note-input" />
            </div>
            <div>
              <input ref={fileRef} type="file" accept={ACCEPT} className="sr-only" onChange={onPick} data-testid="vedlegg-file-input" />
              <Button type="button" className="h-11 w-full md:w-auto" onClick={() => fileRef.current?.click()} disabled={upload.isPending} data-testid="vedlegg-upload-button">
                <Upload className="mr-2 h-4 w-4" /> {upload.isPending ? "Lastar opp …" : "Last opp bilete/PDF"}
              </Button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>JPG, PNG, WEBP, HEIC eller PDF. Maks 10 MB.</span>
            {isAdmin ? (
              <label className="flex items-center gap-2 text-sm text-foreground">
                <Switch checked={adminOnly} onCheckedChange={setAdminOnly} data-testid="vedlegg-admin-only-switch" /> Berre synleg for administrasjon
              </label>
            ) : null}
          </div>
        </div>
      ) : null}

      {query.isLoading ? <LoadingList rows={2} /> : null}
      {query.isSuccess && query.data.length === 0 ? <EmptyState icon={Paperclip} title="Ingen vedlegg" description="Last opp bilete eller PDF som dokumenterer arbeidet." data-testid="vedlegg-empty" /> : null}
      {query.isSuccess && query.data.length > 0 ? (
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card" data-testid="vedlegg-list">
          {query.data.map((att) => {
            const Icon = att.content_type === "application/pdf" ? FileText : FileImage;
            const own = att.uploaded_by === user?.id;
            return (
              <div key={att.id} className="flex min-h-[56px] items-center gap-3 p-3 md:p-4" data-testid="vedlegg-item">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary text-primary"><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <button type="button" onClick={() => view(att)} className="block max-w-full truncate text-left text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="vedlegg-open-button">
                    {att.filename}
                  </button>
                  <p className="text-xs text-muted-foreground">
                    <span className="num">{fmtBytes(att.size)}</span> · {att.uploaded_by_name} · <span className="num">{fmtDateTime(att.uploaded_at)}</span>
                    {att.admin_only ? <span className="ml-2 inline-flex items-center gap-1 text-warning"><Lock className="h-3 w-3" /> Berre administrasjon</span> : null}
                  </p>
                  {att.note ? <p className="mt-0.5 text-sm">{att.note}</p> : null}
                </div>
                <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => view(att)} aria-label="Opne" data-testid="vedlegg-download-button"><Download className="h-4 w-4" /></Button>
                {canEdit && (isAdmin || own) ? (
                  <Button variant="ghost" size="icon" className="h-10 w-10 text-destructive hover:text-destructive" onClick={() => remove.mutate(att.id)} aria-label="Slett" data-testid="vedlegg-delete-button"><Trash2 className="h-4 w-4" /></Button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
